/**
 * Analytics utility functions for tracking page views, performance, and feature discovery
 */

import { trackEvent } from '../services/analyticsManager'

// Define types for analytics parameters
type BaseAnalyticsParams = {
  timestamp?: number
}

type PageViewParams = BaseAnalyticsParams & {
  page: string
  gameMode?: string
  [key: string]: string | number | boolean | undefined
}

type PerformanceParams = BaseAnalyticsParams & {
  metric: string
  value: number
  page: string
  gameMode?: string
  [key: string]: string | number | boolean | undefined
}

type FeatureDiscoveryParams = BaseAnalyticsParams & {
  feature: string
  gameMode?: string
  [key: string]: string | number | boolean | undefined
}

/**
 * Track a page view
 *
 * @param params - Page view parameters
 */
export const trackPageView = (params: PageViewParams) => {
  const { page, gameMode, ...restParams } = params

  trackEvent('page_view', {
    page_path: page,
    page_title: getPageTitle(page, gameMode),
    timestamp: Date.now(),
    game_mode: gameMode || null,
    ...restParams,
  })
}

/**
 * Track performance metrics
 *
 * @param params - Performance metrics parameters
 */
export const trackPerformance = (params: PerformanceParams) => {
  const { metric, value, page, gameMode, ...restParams } = params

  trackEvent('performance_metric', {
    metric_name: metric,
    metric_value: value,
    page_path: page,
    timestamp: Date.now(),
    game_mode: gameMode || null,
    ...restParams,
  })
}

/**
 * Track feature discovery
 *
 * @param params - Feature discovery parameters
 */
export const trackFeatureDiscovery = (params: FeatureDiscoveryParams) => {
  const { feature, gameMode, ...restParams } = params

  trackEvent('feature_discovery', {
    feature_name: feature,
    timestamp: Date.now(),
    game_mode: gameMode || null,
    ...restParams,
  })
}

/**
 * Get a readable page title from pathname
 */
function getPageTitle(page: string, gameMode?: string): string {
  switch (page) {
    case 'home':
      return 'Home - Mode Selection'
    case 'game':
      return gameMode ? `${gameMode}-Letter Challenge` : 'Game'
    case 'complete':
      return 'Game Completion'
    default:
      return page.charAt(0).toUpperCase() + page.slice(1)
  }
}
